import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { postingKeyRefSchema } from "../accounting/posting-keys.js";
import { balanceSideSchema } from "../accounting/accounting.js";
import { reconciliationClassificationSchema } from "../fuel/reconciliation.js";
import { taxRateCodeSchema } from "../masterdata/tax.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { dayClosingStatusSchema, fiscalDeviceScopeSchema, shiftLedgerStatusSchema, shiftStatusSchema, shiftTenderTypeSchema, } from "./enums.js";
import { shiftManualSaleSchema } from "./closings.js";
/**
 * Gün sonu kontrol listesi, gün kilidi ve kilitli güne düzeltme kaydı (Prompt 13 — API C aşaması).
 *
 * Değişmezler (ADR 0013 karar 7 ve 12):
 *  · Gün sonu ÖN KOŞULLARI kontrol listesi olarak hesaplanır; her madde ayarla ENGEL ya da UYARI'dır ve
 *    kapanışta DONDURULUR.
 *  · Kapanış TEK transaction'dadır: gelir / KDV / tahsilat özet fişi + POS batch'leri + akaryakıt mutabakatı
 *    ve SMM fişi + gün kilidi + `shifts.day.closed` olayı.
 *  · Kilit AÇILMAZ. Kilitli güne değişiklik yalnızca DÜZELTME KAYDIYLA (ayrı, değiştirilemez belge) yapılır.
 */
export const DAY_END_CHECK_CODES = [
    "PREVIOUS_DAY_OPEN",
    "NO_SHIFTS",
    "SHIFTS_NOT_APPROVED",
    "PENDING_DIFFERENCE_APPROVALS",
    "TANK_CLOSING_READINGS",
    "Z_REPORTS",
    "Z_REPORT_MISMATCH",
    "FUEL_RECONCILIATION",
];
export const dayEndCheckCodeSchema = z.enum(DAY_END_CHECK_CODES);
export const DAY_END_CHECK_LABELS = {
    PREVIOUS_DAY_OPEN: "Daha eski bir iş günü hâlâ kapatılmamış",
    NO_SHIFTS: "Bu iş gününde hiç vardiya yok (istasyon kapalıydı)",
    SHIFTS_NOT_APPROVED: "Tüm vardiyalar onaylanmamış",
    PENDING_DIFFERENCE_APPROVALS: "Onay bekleyen vardiya farkı var",
    TANK_CLOSING_READINGS: "Tank kapanış okuması eksik",
    Z_REPORTS: "Z raporu girilmemiş ÖKC var",
    Z_REPORT_MISMATCH: "Z raporu ile sistem satışı arasında çözülmemiş fark var",
    FUEL_RECONCILIATION: "Akaryakıt mutabakatında onaylanmamış şüpheli / kritik satır var",
};
/**
 * Madde GEÇTİĞİNDE (`OK`) gösterilen etiket. Yukarıdakiler SORUN cümleleridir; "Tamam" rozetinin yanında
 * "Tüm vardiyalar onaylanmamış" yazması kullanıcıyı yanıltıyordu (ekran görüntüsü denetiminde görüldü).
 */
export const DAY_END_CHECK_OK_LABELS = {
    PREVIOUS_DAY_OPEN: "Daha eski iş günlerinin tamamı kapalı",
    NO_SHIFTS: "Bu iş gününde vardiya var",
    SHIFTS_NOT_APPROVED: "Tüm vardiyalar onaylandı",
    PENDING_DIFFERENCE_APPROVALS: "Onay bekleyen vardiya farkı yok",
    TANK_CLOSING_READINGS: "Tank kapanış okumaları tam",
    Z_REPORTS: "Tüm ÖKC'lerin Z raporu girildi",
    Z_REPORT_MISMATCH: "Z raporları sistem satışıyla uyumlu (ya da farklar çözüldü)",
    FUEL_RECONCILIATION: "Akaryakıt mutabakatında onay bekleyen şüpheli / kritik satır yok",
};
/** Duruma göre etiket: geçen maddede olumlu cümle, uyarı / engelde sorun cümlesi. */
export function dayEndCheckLabel(code, status) {
    return status === "OK" ? DAY_END_CHECK_OK_LABELS[code] : DAY_END_CHECK_LABELS[code];
}
export const DAY_END_CHECK_STATUSES = ["OK", "WARNING", "BLOCKING"];
export const dayEndCheckStatusSchema = z.enum(DAY_END_CHECK_STATUSES);
export const dayEndCheckItemSchema = z.object({
    code: dayEndCheckCodeSchema,
    status: dayEndCheckStatusSchema,
    label: z.string(),
    missing: z.number().int().nonnegative(),
    refs: z.array(z.string()).describe("Eksiklerin etiketleri (arayüz listeler)"),
});
export const dayEndChecklistSchema = z.object({
    canClose: z.boolean(),
    items: z.array(dayEndCheckItemSchema),
});
// ── Gün toplamları ───────────────────────────────────────────────────────────
export const dayProductTotalSchema = z.object({
    productId: uuidSchema,
    productName: z.string(),
    revenueGroup: z.string().describe("Ürünün muhasebe grubu (`SALES_REVENUE:{grup}`)"),
    vatRateCode: taxRateCodeSchema,
    businessUnit: businessUnitTypeSchema,
    saleLitres: quantitySchema,
    gross: moneySchema.describe("KDV dâhil beklenen tutar (kuruş)"),
});
export const dayCollectionTotalSchema = z.object({
    /** Nakit için `CASH`, diğerleri için tahsilat türü. */
    kind: z.union([z.literal("CASH"), shiftTenderTypeSchema]),
    amount: moneySchema,
    /** POS terminali / kasa / cari kimliği (varsa). */
    refId: uuidSchema.nullable(),
    refName: z.string().nullable(),
});
export const dayTotalsSchema = z.object({
    shiftCount: z.number().int().nonnegative(),
    closingCount: z.number().int().nonnegative(),
    expectedAmount: moneySchema,
    declaredTotal: moneySchema,
    shortage: moneySchema.describe("Açıkların toplamı (mutlak)"),
    surplus: moneySchema,
    saleLitres: quantitySchema,
    byProduct: z.array(dayProductTotalSchema),
    manualSales: z.array(shiftManualSaleSchema).describe("Kasiyer hesaplarının elle satış özeti"),
    collections: z.array(dayCollectionTotalSchema),
    warnings: z.array(z.string()).describe("Kapanışta oluşan uyarılar (Türkçe metin)"),
});
// ── Kontrol listesi önizlemesi ───────────────────────────────────────────────
export const dayShiftSummarySchema = z.object({
    id: uuidSchema,
    shiftNo: z.string(),
    name: z.string(),
    status: shiftStatusSchema,
    openedAt: timestampSchema,
    closingStartedAt: timestampSchema.nullable(),
    closingCount: z.number().int().nonnegative(),
    acceptedCount: z.number().int().nonnegative(),
});
export const dayZReportSummarySchema = z.object({
    id: uuidSchema.nullable().describe("Rapor girilmemişse null"),
    fiscalDeviceId: uuidSchema,
    serialNo: z.string(),
    label: z.string(),
    scope: fiscalDeviceScopeSchema,
    zNo: z.number().int().nullable(),
    totalAmount: moneySchema.nullable(),
    systemAmount: moneySchema.nullable(),
    difference: moneySchema.nullable(),
    mismatch: z.boolean(),
    resolved: z.boolean(),
});
export const dayFuelReconciliationSchema = z.object({
    tankId: uuidSchema,
    tankNo: z.number().int(),
    classification: reconciliationClassificationSchema,
    approved: z.boolean(),
    varianceL: quantitySchema,
});
export const dayChecklistViewSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    businessDate: localDateSchema,
    status: dayClosingStatusSchema,
    dayClosingId: uuidSchema.nullable(),
    checklist: dayEndChecklistSchema,
    totals: dayTotalsSchema,
    shifts: z.array(dayShiftSummarySchema),
    zReports: z.array(dayZReportSummarySchema),
    fuelReconciliation: z.array(dayFuelReconciliationSchema),
    /** Akaryakıt tarafının kapatılabilmesi için takvim gününün bitmiş olması gerekir. */
    calendarDayFinished: z.boolean(),
});
export const dayChecklistQuerySchema = z.object({
    stationId: uuidSchema,
    businessDate: localDateSchema,
});
// ── Gün kapanışı (kilit) ─────────────────────────────────────────────────────
const dayClosingCore = {
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    businessDate: localDateSchema,
    status: dayClosingStatusSchema,
    salesEntryId: uuidSchema.nullable(),
    salesLedgerStatus: shiftLedgerStatusSchema,
    closedAt: timestampSchema.nullable(),
    closedBy: uuidSchema.nullable(),
    note: z.string().nullable(),
    version: z.number().int().min(1),
};
export const businessDaySummarySchema = z.object({
    ...dayClosingCore,
    shiftCount: z.number().int().nonnegative(),
    expectedAmount: moneySchema,
    shortage: moneySchema,
    surplus: moneySchema,
});
export const businessDayPageSchema = pageOf(businessDaySummarySchema);
export const listBusinessDaysQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    status: dayClosingStatusSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const dayCorrectionLineSchema = z.object({
    key: postingKeyRefSchema,
    side: balanceSideSchema,
    amount: nonNegativeMoneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır"),
    counterpartyId: uuidSchema.nullable().optional(),
    businessUnit: businessUnitTypeSchema.nullable().optional(),
});
export const dayCorrectionSchema = z.object({
    id: uuidSchema,
    dayClosingId: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    correctionNo: z.string().describe("GSD{yıl}{sıra} — boşluksuz"),
    reason: z.string(),
    lines: z.array(dayCorrectionLineSchema),
    amount: moneySchema.describe("Borç toplamı (kuruş)"),
    postingDate: localDateSchema,
    journalEntryId: uuidSchema.nullable(),
    ledgerStatus: shiftLedgerStatusSchema,
    createdBy: uuidSchema,
    createdAt: timestampSchema,
});
export const dayCorrectionListSchema = z.object({ items: z.array(dayCorrectionSchema) });
export const createDayCorrectionSchema = z.object({
    reason: z.string().trim().min(10).max(1000).describe("Düzeltmenin gerekçesi (en az 10 karakter)"),
    lines: z.array(dayCorrectionLineSchema).min(2).max(200),
    postingDate: localDateSchema
        .optional()
        .describe("Yoksa iş günü; iş gününün dönemi kapalıysa açık bir tarih verilmelidir"),
});
export const businessDayDetailSchema = z.object({
    ...dayClosingCore,
    /** Kapanışta DONDURULAN kontrol listesi (gün açıksa canlı hesaplanır). */
    checklist: dayEndChecklistSchema,
    totals: dayTotalsSchema,
    shifts: z.array(dayShiftSummarySchema),
    zReports: z.array(dayZReportSummarySchema),
    corrections: z.array(dayCorrectionSchema),
    /** Gün sonunda oluşan POS batch'leri (hazine modülünde; fiş kesmezler). */
    posBatchIds: z.array(uuidSchema),
});
export const closeBusinessDaySchema = z.object({
    stationId: uuidSchema,
    businessDate: localDateSchema,
    note: z.string().trim().min(1).max(1000).nullable().optional(),
});
export const businessDayParamsSchema = z.object({ id: uuidSchema });
//# sourceMappingURL=day-end.js.map